import { useEffect, useState } from "react";
import { type LabLetterhead, LETTERHEAD_LIMITS, type LetterheadContentType, type LetterheadInfo } from "@probetalab/contracts";
import { get, getBlob, run } from "../lib/api.js";
import { useSession } from "../lib/session.js";
import { blobToBase64 } from "../lib/signature.js";
import { useToast } from "../lib/toast.js";
import { errorMessage, useQuery } from "../lib/useQuery.js";
import { Alert, Button, Card, Definition, Empty, Field, Input, Loading, Switch } from "./ui.js";

/**
 * Configuración → Membrete (migration 0023, ADR 0024). The Administrador uploads the laboratory's
 * letterhead (a one-page PDF, or a PNG/JPG of the page); every new result report is printed on it.
 * The free zone ("zona libre") — how far from the top and bottom edges the report may write — is
 * adjusted with a live preview of a sample report, and the same zone is used "sin membrete", for
 * paper that already carries the letterhead. Reports already issued do not change.
 */

const PT_PER_CM = 72 / 2.54;
export const ptToCm = (pt: number) => Math.round((pt / PT_PER_CM) * 10) / 10;
export const cmToPt = (cm: number) => Math.round(cm * PT_PER_CM);

const ACCEPT: Record<string, LetterheadContentType> = { "application/pdf": "application/pdf", "image/png": "image/png", "image/jpeg": "image/jpeg" };

/** Why a file cannot be uploaded, in es-MX, or null. Checked before reading it; the API re-checks everything. */
export function letterheadFileProblem(file: { size: number; type: string }): string | null {
  if (!ACCEPT[file.type]) return "El membrete debe ser un PDF, PNG o JPG.";
  if (file.size === 0) return "El archivo está vacío.";
  if (file.size > LETTERHEAD_LIMITS.maxBytes) return "El membrete pesa más de 2 MB. Expórtalo de nuevo con menos resolución.";
  return null;
}

/** Why a "Recortar orilla" value cannot be saved, or null (mirrors the API: 0-5 mm, in tenths). */
export function insetProblem(mm: number): string | null {
  if (!Number.isFinite(mm) || mm < 0 || mm > LETTERHEAD_LIMITS.maxEdgeInsetMm || Math.abs(mm * 10 - Math.round(mm * 10)) > 1e-9) return "Recortar orilla va de 0 a 5 mm.";
  return null;
}

/** Why a zone cannot be saved, or null (mirrors the API). */
export function zoneProblem(top: number, bottom: number): string | null {
  const { minMarginPt, maxMarginPt, minBodyPt } = LETTERHEAD_LIMITS;
  if ([top, bottom].some((v) => !Number.isFinite(v) || v < minMarginPt || v > maxMarginPt)) return "Cada margen va de 1.3 a 11.4 cm.";
  if (792 - top - bottom < minBodyPt) return "La zona libre es demasiado chica: deja al menos 13 cm para los resultados.";
  return null;
}

export function LetterheadCard() {
  const { isOperator } = useSession();
  const toast = useToast();
  const current = useQuery(() => get("labLetterhead"), []);
  const info = current.data?.letterhead ?? null;
  const [busy, setBusy] = useState<"upload" | "zone" | "remove" | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  async function upload(f: File | undefined) {
    if (!f) return;
    const problem = letterheadFileProblem(f);
    setFileError(problem);
    if (problem) return;
    setBusy("upload");
    try {
      await run<"letterhead.set", LabLetterhead>("letterhead.set", {
        file: { content_type: ACCEPT[f.type]!, base64: await blobToBase64(f) },
        content_top_pt: info?.content_top_pt ?? LETTERHEAD_LIMITS.defaultTopPt,
        content_bottom_pt: info?.content_bottom_pt ?? LETTERHEAD_LIMITS.defaultBottomPt,
        edge_inset_mm: info?.edge_inset_mm ?? 0,
      });
      await current.reload();
      toast.success(info ? "Membrete reemplazado." : "Membrete guardado. Revisa la zona libre en la vista previa.");
    } catch (e) {
      setFileError(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (!window.confirm("¿Quitar el membrete? Los informes nuevos volverán al encabezado sencillo con el nombre del laboratorio y el domicilio de la sucursal. Los ya emitidos no cambian.")) return;
    setBusy("remove");
    try {
      await run("letterhead.remove", { letterhead_id: info!.id });
      await current.reload();
      toast.success("Membrete quitado.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  const picker = (label: string) => (
    <label className={`btn ${info ? "btn-secondary" : "btn-primary"}`} style={{ justifySelf: "start", position: "relative" }} aria-disabled={busy !== null}>
      {busy === "upload" ? "Subiendo…" : label}
      <input
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        aria-label="Archivo del membrete"
        className="sr-only"
        disabled={busy !== null}
        onChange={(e) => {
          void upload(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </label>
  );

  return (
    <Card
      title="Membrete de los resultados"
      actions={
        info && !isOperator ? (
          <Button variant="ghost" icon="x" busy={busy === "remove"} disabled={busy !== null} onClick={() => void remove()}>
            Quitar membrete
          </Button>
        ) : undefined
      }
    >
      <div className="stack-lg">
        <p className="muted small" style={{ margin: 0 }}>
          Los informes nuevos se imprimen sobre el membrete del laboratorio. Debe incluir el nombre del laboratorio, los domicilios y el nombre y la cédula del responsable sanitario: con
          membrete, el informe ya no los repite. Los informes ya emitidos no cambian.
        </p>
        {current.loading && !current.data ? (
          <Loading />
        ) : !info ? (
          <Empty icon="doc" title="Sin membrete" hint="Por ahora los informes llevan un encabezado sencillo con el nombre del laboratorio y el domicilio de la sucursal.">
            {!isOperator && <div className="stack" style={{ gap: 6, justifyItems: "center" }}>{picker("Subir membrete")}</div>}
          </Empty>
        ) : (
          <ZoneEditor info={info} readOnly={isOperator} onSaved={() => void current.reload()} uploadButton={!isOperator ? picker("Reemplazar archivo") : null} />
        )}
        {!info && !isOperator && <p className="small muted" style={{ margin: 0 }}>PDF de una página, tamaño carta (de preferencia, se imprime nítido), o PNG/JPG de la hoja completa. Hasta 2 MB.</p>}
        {fileError && <Alert tone="red">{fileError}</Alert>}
      </div>
    </Card>
  );
}

function ZoneEditor({ info, readOnly, onSaved, uploadButton }: { info: LetterheadInfo; readOnly: boolean; onSaved: () => void; uploadButton: React.ReactNode }) {
  const toast = useToast();
  const [top, setTop] = useState(info.content_top_pt);
  const [bottom, setBottom] = useState(info.content_bottom_pt);
  const [topText, setTopText] = useState(ptToCm(info.content_top_pt).toFixed(1));
  const [bottomText, setBottomText] = useState(ptToCm(info.content_bottom_pt).toFixed(1));
  const [inset, setInset] = useState(info.edge_inset_mm);
  const [insetText, setInsetText] = useState(info.edge_inset_mm.toFixed(1));
  const [withBackground, setWithBackground] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTop(info.content_top_pt);
    setBottom(info.content_bottom_pt);
    setTopText(ptToCm(info.content_top_pt).toFixed(1));
    setBottomText(ptToCm(info.content_bottom_pt).toFixed(1));
    setInset(info.edge_inset_mm);
    setInsetText(info.edge_inset_mm.toFixed(1));
  }, [info.id, info.content_top_pt, info.content_bottom_pt, info.edge_inset_mm]);

  const problem = zoneProblem(top, bottom) ?? insetProblem(inset);
  const dirty = top !== info.content_top_pt || bottom !== info.content_bottom_pt || inset !== info.edge_inset_mm;

  // The live preview: a sample report on the letterhead with the zone as it is being edited.
  useEffect(() => {
    if (problem || readOnly) return;
    let url: string | null = null;
    let cancelled = false;
    const t = window.setTimeout(() => {
      getBlob("labLetterheadSample", { query: { top, bottom, inset, letterhead: withBackground } })
        .then((b) => {
          if (cancelled) return;
          url = URL.createObjectURL(b);
          setPdfUrl(url);
          setPreviewError(null);
        })
        .catch((e) => !cancelled && setPreviewError(errorMessage(e)));
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
      if (url) URL.revokeObjectURL(url);
    };
  }, [top, bottom, inset, withBackground, problem, readOnly, info.sha256_hex]);

  const setCm = (which: "top" | "bottom", text: string) => {
    (which === "top" ? setTopText : setBottomText)(text);
    const cm = Number(text.replace(",", "."));
    if (text.trim() && Number.isFinite(cm)) (which === "top" ? setTop : setBottom)(cmToPt(cm));
  };
  const setInsetMm = (text: string) => {
    setInsetText(text);
    const mm = Number(text.replace(",", "."));
    if (text.trim() && Number.isFinite(mm)) setInset(Math.round(mm * 10) / 10);
  };
  const stepInset = (delta: number) => {
    const mm = Math.max(0, Math.min(LETTERHEAD_LIMITS.maxEdgeInsetMm, Math.round((inset + delta) * 10) / 10));
    setInset(mm);
    setInsetText(mm.toFixed(1));
  };
  const step = (which: "top" | "bottom", delta: number) => {
    const pt = Math.max(LETTERHEAD_LIMITS.minMarginPt, Math.min(LETTERHEAD_LIMITS.maxMarginPt, (which === "top" ? top : bottom) + delta));
    (which === "top" ? setTop : setBottom)(pt);
    (which === "top" ? setTopText : setBottomText)(ptToCm(pt).toFixed(1));
  };

  async function save() {
    setSaving(true);
    try {
      await run("letterhead.set", { content_top_pt: top, content_bottom_pt: bottom, edge_inset_mm: inset });
      onSaved();
      toast.success("Cambios guardados. Los informes nuevos los usan.");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const kind = info.content_type === "application/pdf" ? "PDF" : info.content_type === "image/png" ? "PNG" : "JPG";
  const size = info.content_type === "application/pdf" ? `${ptToCm(info.width)} × ${ptToCm(info.height)} cm` : `${info.width} × ${info.height} px`;

  if (readOnly) {
    return (
      <div className="stack">
        <Definition k="Archivo" v={`${kind} · ${size}`} />
        <Definition k="Zona libre" v={`${ptToCm(info.content_top_pt)} cm arriba · ${ptToCm(info.content_bottom_pt)} cm abajo`} />
        <Definition k="Recortar orilla" v={`${info.edge_inset_mm} mm`} />
      </div>
    );
  }

  const marginField = (which: "top" | "bottom", label: string, hint: string) => (
    <Field label={label} hint={hint}>
      <div className="row" style={{ gap: 6 }}>
        <Button variant="secondary" size="sm" aria-label={`${label}: menos`} onClick={() => step(which, -cmToPt(0.1) || -3)}>
          −
        </Button>
        <Input
          inputMode="decimal"
          value={which === "top" ? topText : bottomText}
          onChange={(e) => setCm(which, e.target.value)}
          aria-label={`${label} en centímetros`}
          style={{ width: 80, textAlign: "center" }}
        />
        <span className="muted small">cm</span>
        <Button variant="secondary" size="sm" aria-label={`${label}: más`} onClick={() => step(which, cmToPt(0.1) || 3)}>
          +
        </Button>
      </div>
    </Field>
  );

  return (
    <div className="split">
      <div className="stack-lg">
        <div className="stack" style={{ gap: 6 }}>
          <Definition k="Archivo" v={`${kind} · ${size}`} />
          {uploadButton}
        </div>
        <div className="stack">
          <strong>Zona libre</strong>
          <p className="small muted" style={{ margin: 0 }}>
            Dónde empieza y dónde termina lo que imprime el informe, para no encimarse con el encabezado ni con el pie del membrete. Muévela hasta que en la vista previa el folio quede
            debajo del encabezado y las firmas arriba del pie.
          </p>
          {marginField("top", "Margen superior", "Desde el borde de arriba de la hoja.")}
          {marginField("bottom", "Margen inferior", "Desde el borde de abajo de la hoja.")}
          {problem && <Alert tone="amber">{problem}</Alert>}
          <div className="row" style={{ gap: 8 }}>
            <Button variant="primary" icon="check" busy={saving} disabled={!dirty || !!problem} onClick={() => void save()}>
              Guardar cambios
            </Button>
            {dirty && (
              <Button variant="ghost" onClick={() => { setTop(info.content_top_pt); setBottom(info.content_bottom_pt); setTopText(ptToCm(info.content_top_pt).toFixed(1)); setBottomText(ptToCm(info.content_bottom_pt).toFixed(1)); setInset(info.edge_inset_mm); setInsetText(info.edge_inset_mm.toFixed(1)); }}>
                Deshacer
              </Button>
            )}
          </div>
        </div>
        <div className="stack">
          <strong>Recortar orilla</strong>
          <p className="small muted" style={{ margin: 0 }}>
            Si el membrete trae un marco o una línea en el borde de la hoja que en la pantalla se ve pero impreso no sale, auméntalo hasta que desaparezca en la vista previa (casi siempre
            basta 1 o 2 mm). En 0 no se recorta nada.
          </p>
          <Field label="Recortar orilla" hint="De 0 a 5 mm, por los cuatro lados.">
            <div className="row" style={{ gap: 6 }}>
              <Button variant="secondary" size="sm" aria-label="Recortar orilla: menos" onClick={() => stepInset(-0.5)}>
                −
              </Button>
              <Input inputMode="decimal" value={insetText} onChange={(e) => setInsetMm(e.target.value)} aria-label="Recortar orilla en milímetros" style={{ width: 80, textAlign: "center" }} />
              <span className="muted small">mm</span>
              <Button variant="secondary" size="sm" aria-label="Recortar orilla: más" onClick={() => stepInset(0.5)}>
                +
              </Button>
            </div>
          </Field>
        </div>
        <Switch checked={withBackground} onChange={setWithBackground}>
          Ver con el membrete <span className="muted small">— apágalo para ver cómo sale en hoja ya membretada</span>
        </Switch>
      </div>
      <div className="sticky">
        {previewError ? <Alert tone="red">{previewError}</Alert> : pdfUrl ? <iframe title="Vista previa del membrete" src={`${pdfUrl}#toolbar=0&view=FitH`} className="pdf-frame" /> : <Loading label="Preparando la vista previa…" />}
      </div>
    </div>
  );
}
